import { redact } from "@/src/lib/logging/redact";

function serializeError(err: unknown): { name?: string; message: string; stack?: string } {
  if (err instanceof Error) return { name: err.name, message: err.message, stack: err.stack };

  return { message: String(err) };
}

// Stats types for Google Alerts
export type StatType = "counter" | "gauge" | "histogram" | "timer";

// Stats interface for structured logging
export interface StatData {
  metric: string;
  value: number;
  type: StatType;
  tags?: Record<string, string | number>;
  unit?: string;
  timestamp?: string;
}

// ANSI color codes for terminal
const COLORS = {
  RESET: "\x1b[0m",
  BRIGHT_BLUE: "\x1b[38;5;33m",
};

export function logStatAsync(statData: StatData, meta?: Record<string, unknown>) {
  Promise.resolve().then(() => {
    try {
      const timestamp = new Date().toISOString();
      const isLocalTerminal = process.env.NODE_ENV !== "production";

      // Create structured stat object
      const statObj = {
        severity: "INFO" as const,
        message: `📊 ${statData.type.toUpperCase()}: ${statData.metric} = ${statData.value}${statData.unit ? ` ${statData.unit}` : ""}`,
        metric: statData.metric,
        value: statData.value,
        type: statData.type,
        tags: statData.tags || {},
        unit: statData.unit,
        timestamp: statData.timestamp || timestamp,
        isStat: true, // Custom field to identify stats
        ...meta,
      };

      let logArgs;
      const processedStatObj = process.env.NODE_ENV === "development" ? statObj : redact(statObj);

      if (isLocalTerminal) {
        return; // No logging in local terminal
      } else {
        // For cloud logs, use structured JSON for easy querying
        logArgs = [JSON.stringify(processedStatObj)];
      }

      console.info(...logArgs);
    } catch (err) {
      console.error(`[LOGGER ERROR] Failed to log stat:`, err);
    }
  });
}

// Convenience functions for common stat types
export const stats = {
  counter: (
    metric: string,
    value: number = 1,
    options?: { tags?: Record<string, string | number>; unit?: string; meta?: Record<string, unknown>; error?: unknown }
  ) => {
    const errorMeta = options?.error !== undefined ? { error: serializeError(options.error) } : {};

    logStatAsync(
      { metric, value, type: "counter", tags: options?.tags, unit: options?.unit },
      { ...options?.meta, ...errorMeta }
    );
  },

  gauge: (
    metric: string,
    value: number,
    options?: { tags?: Record<string, string | number>; unit?: string; meta?: Record<string, unknown> }
  ) => {
    logStatAsync({ metric, value, type: "gauge", tags: options?.tags, unit: options?.unit }, options?.meta);
  },

  histogram: (
    metric: string,
    value: number,
    options?: { tags?: Record<string, string | number>; unit?: string; meta?: Record<string, unknown> }
  ) => {
    logStatAsync({ metric, value, type: "histogram", tags: options?.tags, unit: options?.unit }, options?.meta);
  },

  timer: (
    metric: string,
    value: number,
    options?: { tags?: Record<string, string | number>; unit?: string; meta?: Record<string, unknown> }
  ) => {
    logStatAsync({ metric, value, type: "timer", tags: options?.tags, unit: options?.unit || "ms" }, options?.meta);
  },

  // Generic stat function
  stat: (
    metric: string,
    value: number,
    type: StatType = "counter",
    options?: { tags?: Record<string, string | number>; unit?: string; meta?: Record<string, unknown> }
  ) => {
    logStatAsync({ metric, value, type, tags: options?.tags, unit: options?.unit }, options?.meta);
  },
};
